import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-community";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {dateFormatterShort, fromGenericBeToGenericFe, onGridReady, useActionColDef} from "../../../utils";
import DialogComponent from "../../../common/components/DialogComponent";
import {
    DialogInterface,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE
} from "../../../api/types/generic";
import {AgGridReact} from "ag-grid-react";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {useNavigate} from "react-router-dom";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import Loading from "../../../common/components/Loading";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useExcessGroupRepository} from "../../../api/repositories/ExcessGroupRepository";
import {DEFAULT_PLAN_COL_DEF} from "../../../constants";
import ActionsPopover from "../../../common/components/ActionsPopover";
import {
    useExcessGroupProductCodeLocationGroupRepository
} from "../../../api/repositories/ExcessGroupProductCodeLocationGroupRepository";
import SearchFilters from "../../../common/components/SearchFilters";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {useRecoilState} from "recoil";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {FormSearchAssociationPlanModel} from "../../../api/types/associationPlan";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

type IProductData = {
    id: string;
    productCodeName: string;
}
type IProductLocationGroupData = IProductData & {
    locationGroupId: number | null;
    locationGroupName: string | null;
}

type IExcessGroupPlanData = {
    id: string;
    productCodeName: string;
    locationGroupId: number | null;
    locationGroupName: string | null;
    excessGroup: string | null;
    excessGroupId: number | null;
    validityDateFrom: string | null;
    validityDateTo: string | null;
}

const ExcessProductCodeLocationGroupList = () => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Navigate
    const navigate = useNavigate();

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    // Use Repository Hooks
    const {getPagedProductCode} = useProductCodeRepository();
    const {getLocationGroupsByProductCodePaged} = useProductCodeLocationGroupRepository();
    const {getExcessGroupsPagedProductPlan} = useExcessGroupRepository();
    const {postExcessGroupProductCodeLocationGroupAll} = useExcessGroupProductCodeLocationGroupRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchAssociationPlanModel>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        productCode: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const handleTriggerFilters = (entries: FormSearchAssociationPlanModel) => {
        const hardcodedFilter = Object.entries(entries).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: {type: "contains", filter: value}
            }),
            {}
        );

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: [],
            legalEntities: []
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            commercialCategoryId: (testFilters.commercialCategoryId as SelectElementModelFE)?.code as string,
            commercialSegmentId: (testFilters.commercialSegmentId as SelectElementModelFE)?.code as string,
            productCode: testFilters.productCode,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
            dealers:
                ((testFilters.dealers as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? dealerFilteredOptions
                    : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
        // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
        const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();

        const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryList(commercialCategoryList);

        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] = fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    useEffect(() => {
        const getContent = async () => {
            if (testFilters.commercialSegmentId) {
                const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((testFilters.commercialSegmentId as SelectElementModelFE).code as string);
                const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryList(commercialCategoryList);
            } else {
                // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
                // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
                const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();

                const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryList(commercialCategoryList);
            }
        };
        getContent();
    }, [testFilters.commercialSegmentId]);

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "productCode",
            value: testFilters.productCode,
            label: `${t("pricing:associationPlan.form.labels.productCode")}`,
            handleChange: (value: string) => {
                setTestFilters({
                    ...testFilters,
                    productCode: value
                });
            },
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:associationPlan.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:associationPlan.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete"
        }
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOption || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    // ************ AG GRID START ************ //
    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "id",
            headerName: t("pricing:excessGroupProductCodeLocationGroup.table.headers.productCode"),
            rowGroup: true,
            hide: true,
            pinned: "left",
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.data.id + " - " + params.data.productCodeName
        },
        {
            field: "legalEntities",
            headerName: t("pricing:peaks.table.headers.legalEntities"),
            headerTooltip: t("pricing:upsellingRate.table.headers.legalEntities"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            pinned: "left",
            hide: true
        },
        {
            field: "dealers",
            headerName: t("pricing:peaks.table.headers.dealers"),
            headerTooltip: t("pricing:upsellingRate.table.headers.dealers"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
            pinned: "left",
            hide: true
        },
        {
            field: "locationGroupId",
            headerName: t("pricing:excessGroupProductCodeLocationGroup.table.headers.locationGroup"),
            rowGroup: true,
            hide: true,
            pinned: "left",
            cellRenderer: (params: ICellRendererParams) => params.data.locationGroupName
        },
        {
            field: "excessGroup",
            flex: 1,
            headerName: t("pricing:excessGroupProductCodeLocationGroup.table.headers.excessGroup"),
            headerTooltip: t("pricing:excessGroupProductCodeLocationGroup.table.headers.excessGroup")
        },
        {
            field: "validityDateFrom",
            flex: 1,
            headerName: t("pricing:excessGroupProductCodeLocationGroup.table.headers.validityDateFrom"),
            headerTooltip: t("pricing:excessGroupProductCodeLocationGroup.table.headers.validityDateFrom")
        },
        {
            field: "validityDateTo",
            flex: 1,
            headerName: t("pricing:excessGroupProductCodeLocationGroup.table.headers.validityDateTo"),
            headerTooltip: t("pricing:excessGroupProductCodeLocationGroup.table.headers.validityDateTo")
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => {
                return params.data.excessGroup &&
                    <ActionsPopover
                        params={params}
                        update={{
                            action: () => navigate("/pricing/product-code/location-group-excess-group/create", {
                                replace: true,
                                state: {
                                    excessGroupId: params.data.excessGroupId
                                }
                            }), userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_UPDATE_EXCESS_GROUP_PLAN_LIST"
                                })
                            })
                        }}
                        del={{
                            action: async () => {
                                await postExcessGroupProductCodeLocationGroupAll({
                                    associationsToSave: [],
                                    associationsToDelete: [{
                                        excessGroupId: params.data.excessGroupId,
                                        productCodeId: params.data.id,
                                        locationGroupId: params.data.locationGroupId
                                    }]
                                });
                                gridRef.current!.api.refreshServerSide({purge: true});
                            }, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_DELETE_EXCESS_GROUP_PLAN_LIST"
                                })
                            })
                        }}
                    />;
            }
        },


        {
            field: "productCode",
            colId: "productCode",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialCategoryId",
            colId: "commercialCategoryId",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialSegmentId",
            colId: "commercialSegmentId",
            hide: true,
            filter: "text"
        }
    ];
    const autoGroupColumnDef = useMemo<ColDef>(() => {
        return {
            minWidth: 200
        };
    }, []);

    const defaultColDef = DEFAULT_PLAN_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchAssociationPlanModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);

                let rowCount = 0;
                let rowData: IProductData[] | IProductLocationGroupData[] | IExcessGroupPlanData[] = [];

                // @ts-ignore
                let size = params.request.endRow - params.request.startRow;
                // @ts-ignore
                let page = params.request.startRow > 0 ? Math.floor(params.request.startRow / size) : 0;

                const groupKeys = params.request.groupKeys;
                if (!groupKeys || groupKeys.length === 0) {
                    //caricamento codici prodotto
                    const productCodePaged = await getPagedProductCode({
                        ...filter,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    if (productCodePaged.totalElements === 0) {
                        gridRef.current!.api.showNoRowsOverlay();
                    } else {
                        gridRef.current!.api.hideOverlay();
                    }

                    rowCount = productCodePaged.totalElements;
                    rowData = productCodePaged.content.map(pc => ({
                        id: pc.id as string,
                        productCodeName: pc.name
                    })) as IProductData[];
                }
                //caricamento gruppi di location
                else if (groupKeys.length === 1 && groupKeys[0]) {
                    const locationGroupList = await getLocationGroupsByProductCodePaged({
                        productCode: groupKeys[0],
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    rowCount = locationGroupList.totalElements;
                    rowData = locationGroupList.content?.map(lg => ({
                        id: groupKeys[0] as string,
                        productCodeName: groupKeys[0] as string,
                        locationGroupId: lg.id,
                        locationGroupName: lg.name
                    })) as IProductLocationGroupData[];
                }
                //caricamento excessPlan
                else if (groupKeys.length === 2 && groupKeys[0] && groupKeys[1]) {
                    const excessGroups = await getExcessGroupsPagedProductPlan({
                        productCode: groupKeys[0],
                        locationGroupId: Number(groupKeys[1]),
                        dealers: testFilters.dealers,
                        legalEntities: testFilters.legalEntities,
                        showOnlyActive: true,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    rowCount = excessGroups.totalElements;
                    rowData = excessGroups.content.map(excessGroup => ({
                        id: groupKeys[0] as string,
                        productCodeName: "",
                        locationGroupId: Number(groupKeys[1]),
                        locationGroupName: "",
                        excessGroup: excessGroup.name,
                        excessGroupId: excessGroup.id,
                        validityDateFrom: dateFormatterShort(excessGroup?.validityDateFrom),
                        validityDateTo: dateFormatterShort(excessGroup?.validityDateTo)
                    })) as IExcessGroupPlanData[];
                }

                params.success({
                    rowData: rowData,
                    rowCount: rowCount
                });
            }
        };
    };
    // ************ AG GRID END ************ //

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_EXCESS_GROUP_PLAN_LIST";
                        });
                    })
                        ? {
                            onClick: () =>
                                navigate(
                                    "/pricing/product-code/location-group-excess-group/create"
                                ),
                        }
                        : undefined
                }
            />
        ),
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCESS_GROUP_PLAN_LIST.name")}/>

            <SearchFilters
                filtersOutOfCollapse={filtersOutOfCollapse}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        autoGroupColumnDef={autoGroupColumnDef}
                        animateRows={true}
                        toolbar={toolbar}
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        serverSideFilteringAlwaysResets={true}
                        onGridReady={() => onGridReady({
                            initialSortColId: "name",
                            gridRef,
                            createDatasource
                        })}
                        groupDisplayType={"multipleColumns"}
                    />
                    {dialog?.open &&
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    }
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default ExcessProductCodeLocationGroupList;